/**
 * BottomSheet — generic slide-up sheet for mobile-first overlays.
 *
 * v214.9.3: Created to fix two long-standing pain points:
 *   1. Modals that open without locking body scroll — on iOS Safari, touch
 *      drags on the backdrop scroll the page underneath instead of the modal.
 *   2. Per-modal copy-pasted backdrop/animation/portal boilerplate.
 *
 * Behavior:
 *   - Renders into document.body via createPortal (above all app chrome).
 *   - Locks body scroll while open (preserves scroll position).
 *   - Touch events on the backdrop scroll neither the sheet nor the body.
 *   - Tap on backdrop → onClose. Tap inside sheet → no-op.
 *   - Slide-up animation on mount, fade-in backdrop.
 *   - Inner content area is the actual scroll container, with
 *     overscroll-behavior: contain so iOS rubber-banding doesn't
 *     bubble to the page.
 *
 * Usage:
 *   <BottomSheet open={isOpen} onClose={() => setOpen(false)}>
 *     <header>…</header>
 *     <div className="overflow-y-auto">{scrollable content}</div>
 *   </BottomSheet>
 *
 * Props:
 *   - open: boolean — controls visibility
 *   - onClose: () => void — called when user taps backdrop or close button
 *   - children: ReactNode — sheet body. Caller controls header/scroll/etc.
 *   - maxHeight: string — defaults to '85dvh' (dynamic viewport handles iOS toolbars)
 *   - showHandle: boolean — show the gray drag-handle pill at top (default true)
 *   - zIndex: number — defaults to 9998
 *   - className: string — extra classes on the sheet panel
 */

import { useEffect } from 'react'
import { createPortal } from 'react-dom'

const STYLES = `
  @keyframes bsSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @keyframes bsFadeIn { from { opacity: 0; } to { opacity: 1; } }
`

export default function BottomSheet({
  open,
  onClose,
  children,
  maxHeight = '85dvh',
  showHandle = true,
  zIndex = 9998,
  className = '',
}) {
  // Body scroll lock while open. Preserve scroll position so the page doesn't
  // jump to top when the sheet closes.
  useEffect(() => {
    if (!open) return
    const scrollY = window.scrollY
    const originalOverflow = document.body.style.overflow
    const originalPosition = document.body.style.position
    const originalTop = document.body.style.top
    const originalWidth = document.body.style.width
    document.body.style.overflow = 'hidden'
    // iOS Safari needs position:fixed to actually prevent scroll. Without it,
    // overflow:hidden alone leaks touch-drags through to the page.
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.position = originalPosition
      document.body.style.top = originalTop
      document.body.style.width = originalWidth
      // Restore scroll position
      window.scrollTo(0, scrollY)
    }
  }, [open])

  // Close on Escape (desktop / external keyboards)
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ zIndex }}
      onClick={onClose}
    >
      <style>{STYLES}</style>

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        style={{ animation: 'bsFadeIn 0.2s ease' }}
      />

      {/* Sheet panel */}
      <div
        className={`relative w-full max-w-lg bg-white rounded-t-2xl flex flex-col overflow-hidden bottom-actions [--ba-min:0px] ${className}`}
        style={{
          maxHeight,
          animation: 'bsSlideUp 0.3s ease',
          // iOS rubber-band containment — drags that exceed the scrollable
          // child's bounds stay within the sheet rather than bubbling to body.
          overscrollBehavior: 'contain',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showHandle && (
          <div className="flex justify-center pt-2.5 pb-1.5 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  )
}
